<template>
  <a-modal
    :visible="visible"
    width="450px"
    title="Editar plantilla"
    :keyboard="false"
    centered
    @cancel="handleClose"
  >
    <p>
      Edita el nombre de visualización de tu plantilla cuando desees. 
      El resto de datos, al ser parte de la configuración aprobada de la plantilla, 
      no pueden modificarse.
    </p>
    <a-form layout="vertical">
      <a-form-item label="Nombre de visualización">
        <a-input v-model:value="displayName" />
      </a-form-item>

      <CustomScrollbar class="content" height="calc(100vh - 450px)" auto>
        <section>
          <div class="item">
            <strong>Nombre de la plantilla:</strong>
            <p>{{ template?.name }}</p>
          </div>

          <div class="item">
            <strong>Idioma:</strong>
            <p>{{ template?.language }}</p>
          </div>

          <div class="item">
            <strong>Categoría:</strong>
            <p>{{ template?.category }}</p>
          </div>

          <div class="item">
            <strong>Tipo de plantilla:</strong>
            <p>{{ template?.template_type }}</p>
          </div>

          <div class="item">
            <strong>Estado de plantilla:</strong>
            <p v-if="template?.provider_state === WhatsappTemplateStates.Pending">Pendiente</p>
            <p v-if="template?.provider_state === WhatsappTemplateStates.Rejected">Rechazado</p>
          </div>

          <div class="item" v-if="template?.provider_state === WhatsappTemplateStates.Rejected">
            <strong>Motivo de rechazo de plantilla:</strong>
            <p>{{ template?.reason }}</p>
          </div>

          <div class="item">
            <strong>Contenido de la plantilla:</strong>
            <p>{{ template?.template }}</p>
          </div>

          <div class="item">
            <strong>Parámetros:</strong>
            <p v-for="(param, idx) in template?.parameters" :key="param.name">
              Nombre de parámetro {{ formatParamNumber(idx) }}: {{ param.name }}
            </p>
          </div>

          <div class="item">
            <strong>Botones:</strong>
            <p>{{ template?.buttons?.length ? "Con botones" : "Sin botones"}}</p>
          </div>

          <div class="item" v-if="template?.buttons?.[0]?.type">
            <strong>Tipo de botón:</strong>
            <p>{{ typeButtonsText(template?.buttons?.[0]?.type) }}</p>
          </div>

          <div class="item" v-if="template?.buttons?.[0]?.type">
            <strong>Botones configurados:</strong>
            <div v-for="(button, index) in template?.buttons" :key="button.id">
              <div class="button__name" v-if="button.type === WhatsappTemplateTypeButtons.QuickReply">
                {{ (index + 1) + ". " + button.text }}
              </div>
              <div class="button__name" v-else>
                <span class="button__name-type" v-if="button.type === WhatsappTemplateTypeSpecificButtons.PhoneNumber">
                  {{ (index + 1) + ". Llamada: " }} 
                </span>
                <span class="button__name-type" v-if="button.type === WhatsappTemplateTypeSpecificButtons.VoiceCall">
                  {{ (index + 1) + ". Llamada: " }} 
                </span>
                <span class="button__name-type" v-if="button.type === WhatsappTemplateTypeSpecificButtons.Url">
                  {{ (index + 1) + ". URL: " }} 
                </span><br/>
                <div class="button__detail">
                  <span v-if="button.type === WhatsappTemplateTypeSpecificButtons.PhoneNumber">
                    Salida de llamada: Número externo<br/>
                  </span>
                  <span v-if="button.type === WhatsappTemplateTypeSpecificButtons.VoiceCall">
                    Salida de llamada: WhatsApp Voice ({{ associate }})<br/>
                  </span>
                  <span>Texto: {{ button.text }}</span><br/>
                  <a :href="button.url" target="_blank" rel="url-button" 
                    v-if="button.type === WhatsappTemplateTypeSpecificButtons.Url">
                    {{ button.url }}
                  </a>
                  <span v-if="button.type === WhatsappTemplateTypeSpecificButtons.PhoneNumber">
                  Número: {{ button.code_phone }} {{ button.phone }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </CustomScrollbar>
    </a-form>

    <template #footer>
      <a-button @click="handleClose"> Cancelar </a-button>

      <a-button type="primary" :disabled="isDisabled" @click="handleSave">
        Guardar cambios
      </a-button>
    </template>
  </a-modal>
</template>

<script>
import { defineComponent, ref, watch, computed } from "vue";
import { message } from "ant-design-vue";

import { updateTemplate } from "@/app/channels/services";
import { templateTypeCategoriesOptions } from "@/app/channels/utils/selectOptions";

import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
import { WhatsappTemplateStates, WhatsappTemplateTypeButtons, WhatsappTemplateTypeSpecificButtons } from "@/app/channels/utils/enums";

export default defineComponent({
  components: {
    CustomScrollbar,
  },
  props: {
    visible: {
      required: true,
      type: Boolean,
      default: false,
    },
    template: {
      required: true,
      type: Object,
    },
  },
  setup(props, { emit }) {
    const isDisabled = ref(true);
    const displayName = ref(props?.template?.display_name);
    const associate = computed(() => 
      props?.template?.channel_customer?.associate
    );
    
    const formatParamNumber = (idx) => `{{${idx + 1}}}`;

    const handleSave = async () => {
      try {
        await updateTemplate({
          templateId: props.template.id,
          body: { display_name: displayName.value },
        });
        message.success("Se guardaron los cambios");
        emit("update");
      } catch (error) {
        const errorMessage = error?.getErrorMessage() || "Ha courrido un error";

        message.error(errorMessage);
        console.error(error);
      }
      handleClose();
    };

    const handleClose = () => emit("close");

    watch(displayName, () => {
      isDisabled.value = false;
    });

    const typeButtonsText = (type) => {
      if (type == WhatsappTemplateTypeButtons.QuickReply) {
        return 'Respuesta rápida';
      } else {
        return 'Llamada a la acción';
      }
    };

    return {
      templateTypeCategoriesOptions,
      isDisabled,
      displayName,
      associate,
      formatParamNumber,
      handleSave,
      handleClose,
      WhatsappTemplateStates, 
      WhatsappTemplateTypeButtons, 
      WhatsappTemplateTypeSpecificButtons,
      typeButtonsText,
    };
  },
});
</script>

<style lang="sass" scoped>
section
  color: $gray-8
.content
  padding: 15px 0
  margin-bottom: 40px
.item
  margin-bottom: 16px
.button
  &__name
    margin-bottom: 7px
    &-type
      font-weight: 500
  &__detail
    margin-left: 13px
</style>
